/**
 * Customer 컴포넌트 공통 유틸리티 함수
 */

import type { Customer } from "@/types/customer";

/**
 * 고객 표시 이름 결정 (백엔드 CustomerDto.getDisplayName 로직과 동일)
 *
 * 우선순위:
 * 1. 확정된 이름 (nameConfirmed=true && name) → 이름 그대로
 * 2. AI 감지 이름 → 이름 (isAi=true 표시)
 * 3. 일반 이름 → 이름 그대로
 * 4. 이름 없음 → 전화번호 뒷자리 ("고객 *5678")
 */
export function getDisplayName(customer: Customer): {
  name: string;
  isAi: boolean;
} {
  if (
    customer.nameConfirmed &&
    customer.name &&
    customer.name.trim().length > 0
  ) {
    return { name: customer.name, isAi: false };
  }

  if (customer.aiDetectedName && customer.aiDetectedName.trim().length > 0) {
    return { name: customer.aiDetectedName, isAi: true };
  }

  if (customer.name && customer.name.trim().length > 0) {
    return { name: customer.name, isAi: false };
  }

  // 이름 없음: 전화번호 뒷자리로 표시
  if (customer.phone) {
    const digits = customer.phone.replace(/\D/g, "");
    const last4 = digits.slice(-4);
    return { name: `고객 *${last4}`, isAi: false };
  }

  return { name: "이름 없음", isAi: false };
}

/**
 * 전화번호 포맷팅
 * @example formatPhoneNumber('01012345678') => '010-1234-5678'
 */
export const formatPhoneNumber = (phone: string | null | undefined): string => {
  if (!phone) return "-";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 11) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
  } else if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

/**
 * 전화번호를 0 접두 national 숫자열로 정규화.
 * 하이픈/공백 제거 + +82 국가코드를 0 접두로 변환. (예: '+82 10-1234-5678' → '01012345678')
 */
const toNationalDigits = (phone: string | null | undefined): string => {
  let digits = (phone ?? "").replace(/\D/g, "");
  if (digits.startsWith("82")) {
    digits = "0" + digits.slice(2);
  }
  return digits;
};

/**
 * 두 전화번호가 (정규화 후) 서로 다른지 판정.
 *
 * 실제 발신번호(caller_id)와, 통화 후 분석이 추출/저장한 고객 전화번호를 비교한다.
 * STT 숫자 오인식 등으로 고객 레코드에 실제 발신자와 다른 번호가 들어간 경우를
 * 운영자에게 "확인 필요"로 안내하기 위한 비교 로직.
 *
 * - 둘 중 하나라도 비어 있으면 비교 불가 → false (경고 안 띄움)
 * - 하이픈/공백 등 포맷 차이는 무시(숫자만 비교)
 * - +82 국가코드는 0 접두 national 표기로 정규화 후 비교
 */
export const phoneNumbersDiffer = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean => {
  const da = toNationalDigits(a);
  const db = toNationalDigits(b);
  if (!da || !db) return false;
  return da !== db;
};

/**
 * 생년월일 포맷팅 (YYMMDD → YY.MM.DD)
 * @example formatBirthDate('901225') => '90.12.25'
 */
export const formatBirthDate = (
  birthDate: string | null | undefined,
): string => {
  if (!birthDate) return "-";
  if (birthDate.length === 6) {
    return `${birthDate.slice(0, 2)}.${birthDate.slice(2, 4)}.${birthDate.slice(4)}`;
  }
  return birthDate;
};

/**
 * 날짜 포맷팅 (요일 포함)
 * V206: 프로젝트 전체에서 요일 표시 일관성을 위해 요일 추가
 * @example formatDate('2024-01-15T10:30:00') => '2024. 01. 15. (수)'
 */
const DAY_NAMES = ["일", "월", "화", "수", "목", "금", "토"];
export const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  const dayOfWeek = DAY_NAMES[date.getDay()];
  return `${date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })} (${dayOfWeek})`;
};

/**
 * 상대 시간 표시
 * @example getRelativeTime('2024-01-15T10:30:00') => '3일 전'
 */
export const getRelativeTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "오늘";
  if (diffDays === 1) return "어제";
  if (diffDays < 7) return `${diffDays}일 전`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}주 전`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}개월 전`;
  return `${Math.floor(diffDays / 365)}년 전`;
};
